import React, { useState, useEffect } from "react";
import styles from "styles/genai/Edetail.module.scss";
import PrimaryButton from "components/PrimaryButton";
import LinkButton from "components/LinkButton";
import tick from "img/genai/tick.svg";
import suggestion from "img/genai/suggestions.svg";
import viewport from "img/genai/viewport.svg";
import styled from "styled-components";
import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import bulbIcon from "img/genai/bulb.svg";
import { PreviewModal } from "./../PreviewModal";
import moduleimage from "img/genai/modulepic.svg";
import { createMultipleModules } from "components/genAi/api/genapi";
import OverlayPopOverToolTip from "Uitls/OverlayPopOverToolTip";
import CircularProgressBar from "Uitls/CircularProgressBar";
import NetworkErr from "components/NetworkErr";
import NoDataFound from "components/NoDataFound";
import OneByFourLoader from "components/genAi/utils/OneByFourLoader";

export const MultiAnalysePreviewSlides = ({
  selectedSlide,
  setIsSlideRecomModalOpen,
  handleProceedSlides,
  userCollectedMeta,
  onClose,
  onModulesCreated,
  onGoAhead,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlideTab, setActiveSlideTab] = useState(0);

  // Create pageId to slide data mapping
  const pageIdToSlideMap = {};
  if (
    selectedSlide?.slideIds &&
    selectedSlide?.slidenums &&
    selectedSlide?.signedUrls
  ) {
    selectedSlide.slideIds.forEach((pageId, index) => {
      pageIdToSlideMap[pageId] = {
        slideLabel: selectedSlide.slidenums[index],
        signedUrl:
          selectedSlide.signedUrls.find((url) => url.pageId === pageId)
            ?.signedUrl || "",
      };
    });
  }
  const transformElementRationale = (modules, pageIdToSlideMap) => {
    if (!Array.isArray(modules)) return modules;

    return modules.map((module) => {
      const updatedModule = { ...module };

      const fixRationale = (item) => {
        if (!item?.elementRationale || !item?.sourcePageId) return item;

        const pageId = item.sourcePageId;
        const slideLabel = pageIdToSlideMap[pageId]?.slideLabel;

        if (!slideLabel) return item;

        const cleanLabel = slideLabel
          .replace(/slide_/i, "")
          .replace(/slide/i, "Slide");

        item.elementRationale = `This element comes from Slide_${cleanLabel}`;
        return item;
      };

      // Apply to claims
      if (Array.isArray(updatedModule.claims)) {
        updatedModule.claims = updatedModule.claims.map((c) =>
          fixRationale({ ...c })
        );
      }

      // Apply to reusable texts
      if (Array.isArray(updatedModule.reusableTexts)) {
        updatedModule.reusableTexts = updatedModule.reusableTexts.map((r) =>
          fixRationale({ ...r })
        );
      }

      // Apply to components
      if (Array.isArray(updatedModule.components)) {
        updatedModule.components = updatedModule.components.map((c) =>
          fixRationale({ ...c })
        );
      }

      return updatedModule;
    });
  };

  const fetchModules = async () => {
    try {
      setLoading(true);

      // Transform the data before making the API call
      const requestBody = {
        assetId: selectedSlide.assetId,
        pageIds: selectedSlide.slideIds,
        topic: userCollectedMeta[userCollectedMeta.length - 1].topic,
        communication_objective:
          userCollectedMeta[userCollectedMeta.length - 1].objective,
      };

      // Destructure the object when calling the function
      const response = await createMultipleModules(
        requestBody.assetId,
        requestBody.pageIds,
        requestBody.topic,
        requestBody.communication_objective
      );

      if (!response || (Array.isArray(response) && response.length === 0)) {
        setApiData([]);
        setLoading(false);
        return;
      }
      const transformed = transformElementRationale(response, pageIdToSlideMap);
      setApiData(transformed);

      setLoading(false);
      if (onModulesCreated) {
        onModulesCreated(response);
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(err.message || "Failed to create modules");
      setLoading(false);
    }
  };

  // Call API when component mounts
  useEffect(() => {
    if (selectedSlide) {
      fetchModules();
    }
  }, [selectedSlide]);

  // Transform API data to get unique source combinations for left side - EXACT MODULE ORDER
  const getCombinedSlides = () => {
    if (!apiData || !Array.isArray(apiData)) return [];

    const uniqueCombinations = new Map();
    const orderedCombinations = [];

    apiData.forEach((module) => {
      if (module.sourceCombination && Array.isArray(module.sourceCombination)) {
        // Sort the combination to ensure consistent keys
        const sortedCombination = [...module.sourceCombination].sort();
        const combinationKey = sortedCombination.join("-");

        if (!uniqueCombinations.has(combinationKey)) {
          const isMergedSlide = sortedCombination.length > 1;

          // Get slide labels for this combination
          const slideLabels = sortedCombination.map(
            (pageId) =>
              pageIdToSlideMap[pageId]?.slideLabel || `Slide_${pageId}`
          );

          // Create slide entry
          const slideEntry = {
            sourceCombination: sortedCombination,
            slideLabels: slideLabels,
            signedUrls: sortedCombination.map(
              (pageId) => pageIdToSlideMap[pageId]?.signedUrl || ""
            ),
            combinationCount: sortedCombination.length,
            isMergedSlide: isMergedSlide,
            displayLabel: isMergedSlide
              ? slideLabels.join(" + ")
              : slideLabels[0],
          };

          uniqueCombinations.set(combinationKey, slideEntry);
          orderedCombinations.push(slideEntry);
        }
      }
    });

    return orderedCombinations;
  };

  // Get modules for the currently active slide combination
  const getModulesForActiveSlide = () => {
    if (!apiData || !Array.isArray(apiData) || !combinedSlides.length)
      return [];

    const activeCombination = combinedSlides[activeSlideTab]?.sourceCombination;

    return apiData.filter(
      (module) =>
        module.sourceCombination &&
        JSON.stringify(module.sourceCombination.sort()) ===
          JSON.stringify(activeCombination?.sort())
    );
  };

  const combinedSlides = getCombinedSlides();
  const currentSlideModules = getModulesForActiveSlide();

  const processPreviewData = (data) => {
    if (!data) return null;

    if (data.image && data.pageNo) {
      return {
        pageDetails: [
          {
            signedUrl: data.image,
            pageNo: data.pageNo,
          },
        ],
      };
    }

    if (Array.isArray(data.pageDetails)) {
      return data;
    }

    return null;
  };

  const handleImageClick = (slideOrEdetail) => {
    const normalized = processPreviewData(slideOrEdetail);
    if (!normalized) return;

    setPreviewData(normalized);
    setShowModal(true);
  };
  if (loading) {
    const LeftBox = () => {
      return (
        <div className={`${styles.rectangleCard} ${styles.shimmer}`}>
          <div className={`${styles.whiteLine} ${styles.shimmer}`}></div>
          <div className={`${styles.whiteLine} ${styles.shimmer}`}></div>
        </div>
      );
    };

    const RightBox = () => {
      return (
        <div className={`${styles.whiteCard} ${styles.shimmer}`}>
          <div className={`${styles.whiteLine} ${styles.shimmer}`}></div>
          <div className={`${styles.whiteLine} ${styles.shimmer}`}></div>
        </div>
      );
    };

    return (
      <>
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${styles["analyseWidth"]}`}>
            <article
              className={`${styles.modalHead} ${styles["analysePadding"]}`}
            >
              <span className={styles.headerSlideRec}>
                Selected slides for module creation
              </span>
              <span className={styles.secondHeaderSlideRec}>
                Note: Below system generated mapping can be based on following
                parameters: One or more slides mapped to one or more modules,
                Multiple slides mapped to multiple modules.
                <br />
                Disclaimer: These are AI generated recommendations. Please
                review.
                <br />
              </span>
            </article>

            <article className={styles.ContentSlideRecAnalyse}>
              {/* LEFT SIDE - Rectangle Cards with Shimmer */}
              <div className={styles.contASlideAnalyse}>
                {[1, 2].map((item) => (
                  <LeftBox key={item} />
                ))}
              </div>

              {/* RIGHT SIDE - White Cards with Shimmer */}
              <div className={styles.contBSlideRecAnalyse}>
                <OneByFourLoader
                  loadingSteps={[
                    { text: "Model analysing the elements..." },
                    {
                      text: "Putting together all the elements for the module...",
                    },
                    { text: "Fetching relevant metadata..." },
                    { text: "Fetching images for all the modules..." },
                  ]}
                />
                <article className={styles.multiAnalyseRight}>
                  <>
                    {[1, 2, 3].map((item) => (
                      <RightBox key={item} />
                    ))}
                  </>
                </article>
              </div>
            </article>

            <article className={styles.modalBottom}>
              <div></div>
              <div className={styles.modalBottomRec}>
                <LinkButton title="CANCEL" handleClick={onClose} />
                <PrimaryButton title="CONTINUE" disabled={true} />
              </div>
            </article>
          </div>
        </div>
      </>
    );
  }
  // Create module box with bulb icon and tooltip - YOUR PREFERRED STRUCTURE
  const renderModuleBox = (module, index) => {
    const moduleLabel = module.moduleNumber || `Module_${index + 1}`;

    return (
      <span className={styles.multiModuleanalysebox}>
        <span className={styles.smallModuleBox}>
          <img
            className={styles.imgSlideAnalyse}
            src={moduleimage}
            alt={module.moduleName}
          />
          <span className={styles.analyseText}>{moduleLabel}</span>
        </span>
        <StyledTooltip
          title={module.moduleRationale || ""}
          arrow
          placement="bottom-start"
        >
          <span>
            <img
              src={bulbIcon}
              alt="Show rationale"
              className={styles.bulbIcon}
            />
          </span>
        </StyledTooltip>
      </span>
    );
  };

  if (error) {
    return (
      <div className={styles.modalOverlay}>
        <div className={`${styles.modalContent} ${styles["analyseWidth"]}`}>
          <div className={styles.errorState}>
            <NetworkErr
              title="Error in loading"
              description="We encountered an unexpected error that prevented us from completing the process."
              showButton={true}
              buttonText="Try Again"
              onButtonClick={() => fetchModules()}
            />
          </div>
        </div>
      </div>
    );
  }
  const fallbackSlides =
    selectedSlide?.slideIds?.map((pageId, index) => ({
      sourceCombination: [pageId],
      slideLabels: [selectedSlide.slidenums?.[index] || `Slide_${index + 1}`],
      signedUrls: [
        selectedSlide.signedUrls?.find((url) => url.pageId === pageId)
          ?.signedUrl || "",
      ],
      combinationCount: 1,
      isMergedSlide: false,
      displayLabel: selectedSlide.slidenums?.[index] || `Slide_${index + 1}`,
    })) || [];
  const slidesToRender =
    apiData?.length === 0 ? fallbackSlides : combinedSlides;
  return (
    <>
      <div className={styles.modalOverlay}>
        <div className={`${styles.modalContent} ${styles["analyseWidth"]}`}>
          <article
            className={`${styles.modalHead} ${styles["analysePadding"]}`}
          >
            <span className={styles.headerSlideRec}>
              Selected slides for module creation
            </span>
            <span className={styles.secondHeaderSlideRec}>
              Note: Below system generated mapping can be based on following
              parameters: One or more slides mapped to one or more modules,
              Multiple slides mapped to multiple modules.
              <br />
              Disclaimer: These are AI generated recommendations. Please review.
              <br />
            </span>
          </article>

          <article className={styles.ContentSlideRecAnalyse}>
            {/* LEFT SIDE - Combined Slide Tabs */}
            <div className={styles.contASlideAnalyse}>
              {slidesToRender.map((slide, index) => {
                const isMergedSlide = slide.combinationCount > 1;

                if (isMergedSlide) {
                  return (
                    <div
                      key={slide.sourceCombination.join("-")}
                      className={`${styles.sliderecBoxAnalyse} ${
                        styles.mergedSlide
                      } ${styles.combinationCount} ${
                        styles[`count-${slide.combinationCount}`]
                      } ${activeSlideTab === index ? styles.activeSlide : ""}`}
                      onClick={() => setActiveSlideTab(index)}
                    >
                      {slide.sourceCombination.map((pageId, boxIndex) => (
                        <span
                          key={pageId}
                          className={`${styles[`analysebox${boxIndex + 1}`]} ${
                            activeSlideTab === index ? styles.activeSlide : ""
                          }`}
                        >
                          <img
                            className={styles.imgSlidecont}
                            src={slide.signedUrls[boxIndex]}
                            alt={slide.slideLabels[boxIndex]}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageClick({
                                image: slide.signedUrls[boxIndex],
                                pageNo: boxIndex + 1,
                              });
                            }}
                          />
                          <span className={styles.analyseText}>
                            {slide.slideLabels[boxIndex]}
                          </span>
                        </span>
                      ))}
                      {/* REMOVED: combinedLabel div */}
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={slide.sourceCombination[0]}
                      className={`${styles.sliderecBoxAnalyse} ${
                        activeSlideTab === index ? styles.activeSlide : ""
                      }`}
                      onClick={() => setActiveSlideTab(index)}
                    >
                      <span
                        className={`${styles.analyseboxMulti} ${
                          activeSlideTab === index ? styles.activeSlide : ""
                        }`}
                      >
                        <img
                          className={styles.imgSlidecont}
                          src={slide.signedUrls[0]}
                          alt={slide.displayLabel}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick({
                              image: slide.signedUrls[0],
                              pageNo: index + 1,
                            });
                          }}
                        />
                        <span className={styles.analyseText}>
                          {slide.displayLabel}
                        </span>
                      </span>
                    </div>
                  );
                }
              })}
              {slidesToRender.length > 0 && (
                <div
                  className={styles.leftPlaceholder}
                  style={{
                    height: "8px",
                    flexShrink: 0,
                    width: "100%",
                  }}
                ></div>
              )}
            </div>

            {/* RIGHT SIDE - Modules Only */}
            <div className={styles.contBSlideRecAnalyse}>
              <article className={styles.multiAnalyseRight}>
                {apiData?.length === 0 && (
                  <NoDataFound
                    customClass={true}
                    customClassNoResult={true}
                    title={"No recommendations"}
                    positioning="top: 47%; left: 63%;"
                    Description={
                      "No recommended modules for the selected slides. What would you like to do?"
                    }
                  />
                )}

                {currentSlideModules.map((module, index) => (
                  <div
                    key={`${module.moduleName}-${index}`}
                    className={`${styles.sliderecBoxAnalyse} ${styles.moduleBox}`}
                  >
                    {renderModuleBox(module, index)}
                  </div>
                ))}
              </article>
            </div>
          </article>

          <article className={styles.modalBottom}>
            <div></div>
            <div className={styles.modalBottomRec}>
              {apiData?.length === 0 && (
                <LinkButton title="GO BACK" handleClick={onClose} />
              )}
              <PrimaryButton
                title={
                  apiData?.length === 0
                    ? "CREATE ONE MODULE PER SLIDE"
                    : "CONTINUE"
                }
                handleClick={() => {
                  if (onModulesCreated && apiData) {
                    onModulesCreated(apiData);
                  }
                  onGoAhead();
                }}
                disabled={false}
              />
            </div>
          </article>
        </div>
      </div>
      {previewData && (
        <PreviewModal
          show={showModal}
          onClose={() => setShowModal(false)}
          previewData={previewData}
        />
      )}
    </>
  );
};

const StyledTooltip = withStyles({
  tooltip: {
    backgroundColor: "#fff",
    color: "#333",
    fontSize: "12px",
    padding: "8px 12px",
    borderRadius: "4px",
    whiteSpace: "normal",
    wordWrap: "break-word",
    lineHeight: "1.5",
    marginTop: "0px",
    marginBottom: "8px",
    boxShadow: "0px 0px 8px 0px #231F201F",
    width: "220px",
    height: "auto",
  },
  arrow: {
    color: "#fff",
    width: "35px",
    height: "12px",
    paddingLeft: "9px",
    borderWidth: "2px",
    borderColor: "#fff",
    transform: "scale(1)",
  },

  popper: {
    zIndex: 9999,
    '&[x-placement*="bottom"] .MuiTooltip-tooltip': {
      marginTop: "8px",
      marginBottom: "0px",
    },
    '&[x-placement*="top"] .MuiTooltip-tooltip': {
      marginTop: "0px",
      marginBottom: "8px",
    },
  },
})(Tooltip);
